import { TABS } from '@ui/features/Trade/constants';
import {
  useInvalidateLimitOrderQuery,
  useInvalidatePositionQuery,
} from '@ui/features/Trade/hooks/useInvalidateQuery';
import { useTabsStore } from '@ui/features/Trade/store/useTabsStore';
import { usePositions } from '@ui/hooks/trade/usePositions';
import { useTradeOrders } from '@ui/hooks/trade/useTradeOrders';
import { Tabs } from '@ui/lib/park/ui/tabs';
import { getActiveDecreaseOrders } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/selectors';
import { useDecreaseOrders } from '@ui/services/contract/hooks/TradingPortalFacet/useDecreaseOrders/useDecreaseOrders';
import { useTranslation } from 'next-i18next';
import type { Address } from 'viem';

import { DecreaseOrders } from './DecreaseOrders';
import { Histories } from './Histories';
import { Orders } from './Orders';
import { Positions } from './Positions';

/** #24: the tab bar never scrolls sideways — each table pans on its own */
const contentScrollCss = {
  overscrollBehaviorX: 'contain',
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': { height: '4px' },
  '&::-webkit-scrollbar-thumb': {
    background: 'var(--colors-v2-border-default)',
  },
} as const;

export const BottomTabs = ({
  hideFilters,
  userAddress,
  showHeaderTotals,
}: {
  hideFilters?: boolean;
  userAddress?: Address;
  showHeaderTotals?: boolean;
}) => {
  const { t } = useTranslation();
  const { data: positions } = usePositions(userAddress);
  const { data: orders } = useTradeOrders(userAddress);
  const { data: decreaseOrders } = useDecreaseOrders({ userAddress });
  const currentTab = useTabsStore((s) => s.currentTab);
  const setCurrentTab = useTabsStore((s) => s.setCurrentTab);
  const invalidatePositionQuery = useInvalidatePositionQuery();
  const invalidateLimitOrderQuery = useInvalidateLimitOrderQuery();

  /** V2 tab: 13px/500, inactive muted, active primary — indicator carries the accent */
  const triggerStyleProps = {
    textStyle: 'v2.ui',
    fontSize: '12.5px!',
    fontWeight: '500',
    color: 'v2.text.muted',
    _selected: { color: 'v2.text.primary!' },
    _hover: { color: 'v2.text.primary' },
  } as const;

  return (
    <Tabs.Root
      value={currentTab}
      defaultValue={TABS.Position}
      variant="text"
      size="md"
    >
      <Tabs.List
        px="3"
        py="0"
        h="40px"
        gap="5"
        boxShadow="none"
        borderBottom="1px solid"
        borderBottomColor="v2.border.default"
      >
        <Tabs.Trigger
          {...triggerStyleProps}
          value={TABS.Position}
          onClick={() => {
            setCurrentTab(TABS.Position);
            invalidatePositionQuery();
          }}
          data-cy="Tab-Position"
        >
          {t('common.Position ({count})', {
            count: positions.length,
          })}
        </Tabs.Trigger>

        <Tabs.Trigger
          {...triggerStyleProps}
          value={TABS.Order}
          onClick={() => {
            setCurrentTab(TABS.Order);
            invalidateLimitOrderQuery();
          }}
          data-cy="Tab-Order"
        >
          {t('common.Open Order ({count})', {
            count: orders.length,
          })}
        </Tabs.Trigger>
        <Tabs.Trigger
          {...triggerStyleProps}
          value={TABS.DecreaseOrder}
          onClick={() => {
            setCurrentTab(TABS.DecreaseOrder);
            invalidatePositionQuery();
          }}
          data-cy="Tab-DecreaseOrder"
        >
          {t('common.Decrease Orders ({count})', {
            count: getActiveDecreaseOrders(decreaseOrders).length,
          })}
        </Tabs.Trigger>
        <Tabs.Trigger
          {...triggerStyleProps}
          value={TABS.History}
          onClick={() => setCurrentTab(TABS.History)}
          data-cy="Tab-History"
        >
          {t('common.History')}
        </Tabs.Trigger>
        {/* 2px acid underline sitting on the list's bottom border */}
        <Tabs.Indicator
          bg="v2.long.default"
          h="2px"
          bottom="0"
          width="var(--width)"
        />
      </Tabs.List>
      <Tabs.Content value={TABS.Position} overflowX="auto" css={contentScrollCss}>
        <Positions
          hideFilters={hideFilters}
          userAddress={userAddress}
          showHeaderTotals={showHeaderTotals}
        />
      </Tabs.Content>
      <Tabs.Content value={TABS.Order} overflowX="auto" css={contentScrollCss}>
        <Orders hideFilters={hideFilters} userAddress={userAddress} />
      </Tabs.Content>
      <Tabs.Content value={TABS.DecreaseOrder} overflowX="auto" css={contentScrollCss}>
        <DecreaseOrders hideFilters={hideFilters} userAddress={userAddress} />
      </Tabs.Content>
      <Tabs.Content value={TABS.History} overflowX="auto" css={contentScrollCss}>
        <Histories hideFilters={true} userAddress={userAddress} />
      </Tabs.Content>
    </Tabs.Root>
  );
};
